import { FC, useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { routesPath } from '@router/routes';
import { usePMTaskDefinitionSlice } from '@restore/PMTaskDefinition/PMTaskDefinition.slice';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { PMTaskDefinition } from '@dynamika-shared/types/pmTaskDefinitions';
import { twMerge } from '@utils/twMerge';
import { replacePathParams } from '@utils/replacePathWithParams';
import CreateTaskDrawer, {
  CreateReadingType,
} from 'src/pages/ClinicalTrial/Readings/TaskDefinition/CreateTaskDrawer';
import { TaskDefinitionTableView } from './TaskDefinitionTable.view';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';

type HeaderAction = {
  buttonText: string;
  onClick: () => void;
};

type ReadingsOutletContext = {
  setTaskDefinitionHeaderAction: (action?: HeaderAction) => void;
};

export const TaskDefinition: FC = () => {
  const { setTaskDefinitionHeaderAction } =
    useOutletContext<ReadingsOutletContext>();
  const isPM = useAuthSlice(
    s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER
  );

  const navigate = useNavigate();
  const { id } = useParams();
  const [currentTask, setCurrentTask] = useState<PMTaskDefinition>();
  const [create, setCreate] = useState<boolean>(false);

  const [data, loading, actions] = usePMTaskDefinitionSlice(
    (state, sliceActions) => [state.data, state.loading, sliceActions]
  );

  const deleteTask = async () => {
    if (!currentTask) return;
    actions.deletePMTaskDefinition({
      clinicalTrialId: id as string,
      taskDefinitionId: currentTask.id,
    });
    setCurrentTask(undefined);
  };

  const proceed = (taskData: CreateReadingType) =>
    navigate(routesPath.taskDefinitionForm.replace(':ctId', id as string), {
      state: { formType: 'create', data: taskData },
    });

  useEffect(() => {
    actions.fetchPMTaskDefinitions({ clinicalTrialId: id as string });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTaskDefinitionHeaderAction({
      buttonText: 'Task',
      onClick: () => setCreate(true),
    });

    return () => {
      setTaskDefinitionHeaderAction(undefined);
    };
  }, [setTaskDefinitionHeaderAction]);

  const handleEdit = (task: PMTaskDefinition) => {
    navigate(
      replacePathParams(routesPath.taskDefinitionForm, {
        ctId: id as string,
        taskId: task.id,
      }),
      {
        state: { formType: 'edit', data: task },
      }
    );
  };

  return (
    <div>
      <div className='mt-3'>
        <TaskDefinitionTableView
          data={data}
          loading={loading}
          isPM={isPM}
          onEdit={handleEdit}
          onDelete={task => {
            setCurrentTask(task);
          }}
          onCreate={() => {
            setCreate(true);
          }}
        />
      </div>
      <CreateTaskDrawer
        open={create}
        onClose={() => {
          setCreate(false);
        }}
        onOkClick={proceed}
        onCancelClick={() => {
          setCreate(false);
        }}
      />
      <Modal
        data-testid={'delete-task-definition'}
        open={!!currentTask}
        title='Delete Task'
        onCancel={() => setCurrentTask(undefined)}
        cancelText={'Cancel'}
        onOk={deleteTask}
        okText={'Delete'}
        width={440}
        centered
        maskClosable={false}
        mask={false}
        okClassName={twMerge('ant-btn-dangerous')}
        titleClassName='text-left'
        classNames={{
          header: 'text-left',
          content: 'text-center !rounded-[2.4rem] !p-[4rem]',
          footer: 'flex justify-center',
        }}
      >
        Are you sure you want to delete <br />
        the Task Definition?
      </Modal>
    </div>
  );
};
